// src/components/FeaturedMatch.js

import React, { useState, useContext, useEffect } from "react";
import { FaEdit } from "react-icons/fa";
import API from "../api";
import { UserContext } from "../context/UserContext";

const EMPTY = { title: "", infoLine: "", description: "", imageUrl: "" };

export default function FeaturedMatch() {
  const { isAdmin } = useContext(UserContext);
  const [error, setError] = useState("");
  const [match, setMatch] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    API.getFeaturedMatch()
      .then((data) => {
        if (!cancelled && data) {
          setMatch(data);
          setForm({ ...EMPTY, ...data });
        }
      })
      .catch((err) => console.error("❌ Failed to load featured match:", err))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await API.patch("/admin/featured-match", form);
      setMatch(form);
      setEditing(false);
    } catch (err) {
      console.error("❌ Save failed:", err);
      setError("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!editing && !match?.title && !isAdmin) return null;

  return (
    <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-gray-100">
        <h2 className="text-[15px] font-semibold text-ink">Match to watch</h2>
        {isAdmin && !editing && (
          <button
            onClick={() => setEditing(true)}
            aria-label="Edit featured match"
            className="w-9 h-9 -mr-2 rounded-lg text-gray-500 hover:text-ink hover:bg-gray-100 flex items-center justify-center"
          >
            <FaEdit />
          </button>
        )}
      </div>

      <div className="p-4 md:p-5">
        {error && <p className="mb-3 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-700">{error}</p>}
        {editing ? (
          <div className="space-y-3">
            {[
              { key: "imageUrl", label: "Image URL (direct link)" },
              { key: "title", label: "Title" },
              { key: "infoLine", label: "Info line" },
            ].map(({ key, label }) => (
              <label key={key} className="block">
                <span className="block text-meta font-medium text-gray-700 mb-1">{label}</span>
                <input
                  type="text"
                  value={form[key] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="w-full border border-gray-300 px-3 h-10 rounded-lg focus:border-brand outline-none"
                />
              </label>
            ))}
            <label className="block">
              <span className="block text-meta font-medium text-gray-700 mb-1">Description</span>
              <textarea
                rows={3}
                value={form.description || ""}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:border-brand outline-none"
              />
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="h-10 px-4 rounded-lg bg-brand text-white font-semibold disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => {
                  setForm({ ...EMPTY, ...(match || {}) });
                  setEditing(false);
                }}
                className="h-10 px-4 rounded-lg border border-line text-gray-700 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : !match?.title ? (
          <p className="text-muted text-sm">No featured match set.</p>
        ) : (
          <div className="flex gap-4 lg:flex-col lg:gap-3">
            {match.imageUrl && (
              <img
                key={match.imageUrl}
                src={match.imageUrl}
                alt=""
                className="w-24 h-24 md:w-40 md:h-28 lg:w-full lg:h-40 rounded-lg object-cover shrink-0 bg-gray-100"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-ink">{match.title}</h3>
              {match.infoLine && <p className="text-meta text-muted mt-0.5">{match.infoLine}</p>}
              {match.description && <p className="text-sm text-gray-700 mt-2 line-clamp-3">{match.description}</p>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
